import { useCallback, useEffect, useState, type FormEvent } from "react";
import type { Locale, StaffMember, StaffRosterResponse } from "../../shared/dto";
import { isTestTenant } from "../../shared/test-tenant";
import { OpenClientLink } from "./OpenClient";
import { TestFilterNote } from "./TestFilterNote";
import { formatDate } from "./core";
import { staffMutation, staffMutationErrorKey } from "./model";
import "./admin-selection.css";
import "./admin-team.css";

// ---- Servis wave 4: one member detail (2026-08-05) ----
// The team roster, the "Profil člena týmu" form, the member's clients and
// their access facts used to live in four places, none of which knew about
// the others: the profile form opened on an empty select and the admin had to
// remember WHO they were about to edit. One roster, one detail, one truth.
//
// Every mutation obeys the action feedback law: ready, working, done, error.
// "Done" names the member and the state it left them in, and offers the next
// step instead of a bare "Stav uložen".

export interface TeamMemberProfile {
  photoUrl: string | null;
  calendlyUrl: string | null;
  publicEmail: string | null;
  // F146: no `bio`. The client card carries a fixed sentence, so a member's
  // own description is not something an admin edits either.
  telegramUsername: string | null;
  telegramId: string | null;
  googleAccount: string | null;
  updatedAt: string | null;
}

export interface TeamMemberClient {
  id: string;
  name: string;
  email: string;
  isTest: boolean;
}

export interface TeamMemberActivity {
  id: string;
  type: string;
  createdAt: string;
  clientId: string | null;
  clientName: string | null;
  isTest: boolean;
  summary: Record<string, string>;
}

export interface TeamMemberDetail {
  member: {
    id: string;
    email: string;
    displayName: string | null;
    role: StaffMember["role"];
    isActive: boolean;
    createdAt: string;
    clientCount: number;
    self: boolean;
  };
  profile: TeamMemberProfile;
  missingProfileFields: string[];
  clients: TeamMemberClient[];
  activity: TeamMemberActivity[];
  access: {
    lastSeenAt: string | null;
    activeSessions: number;
    roleLocked: boolean;
    deactivateBlocked: "last_admin" | "self" | null;
    needsHeir: boolean;
    /** `isTest` is the server's verdict over name AND address (audit S2-02). */
    heirs: Array<{ id: string; name: string; email: string; isTest: boolean }>;
  };
}

export interface Roster {
  members: StaffMember[];
  summary: { adminCount: number; partnerCount: number };
  loading: boolean;
  reload: () => Promise<void>;
}

export type TeamActionState = "idle" | "working" | "done" | "error";

export function useRoster(): Roster {
  const [members, setMembers] = useState<StaffMember[]>([]);
  const [summary, setSummary] = useState({ adminCount: 0, partnerCount: 0 });
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch("/api/admin/staff", { credentials: "include" });
      if (!response.ok) throw new Error("roster_failed");
      const body = await response.json() as StaffRosterResponse;
      setMembers(body.members);
      setSummary(body.summary);
    } catch {
      // Keep the last good roster; the panel shows a retry affordance.
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void reload(); }, [reload]);
  return { members, summary, loading, reload };
}

export type TeamFilters = {
  query: string;
  role: "all" | StaffMember["role"];
  activity: "all" | "active" | "inactive";
  locale: Locale;
};

export function filterTeamMembers(members: StaffMember[], filters: TeamFilters): StaffMember[] {
  const needle = filters.query.trim().toLocaleLowerCase(filters.locale);
  return members.filter((member) =>
    (filters.role === "all" || member.role === filters.role)
    && (filters.activity === "all" || (filters.activity === "active") === member.isActive)
    && (!needle || `${member.displayName ?? ""} ${member.email}`.toLocaleLowerCase(filters.locale).includes(needle)));
}

/**
 * 🔴 S3-01 of the third round, S-02 of the first: one sentence, one population.
 *
 * "Zobrazeno záznamů" counted the rows left after the LOCAL filters (search,
 * role, activity) while "Skryto testovacích" counted the global test filter
 * BEFORE them, so "Zobrazeno záznamů: 0. Skryto testovacích: 156." stood over an
 * empty list whose "Zobrazit i testovací" then revealed twenty-nine rows, not a
 * hundred and fifty-six. Both halves are now measured on the same question: how
 * many members match what the reader asked for, and how many of THOSE the test
 * filter is holding back.
 */
export function teamRosterSplit(
  members: StaffMember[],
  filters: TeamFilters,
  hideTestTenants: boolean
): { matching: StaffMember[]; hidden: number; allHiddenByTestFilter: boolean } {
  const shown = hideTestTenants
    ? members.filter((member) => !isTestTenant(member.displayName ?? "", member.email))
    : members;
  const withheld = hideTestTenants ? members.filter((member) => !shown.includes(member)) : [];
  const matching = filterTeamMembers(shown, filters);
  return {
    matching,
    hidden: filterTeamMembers(withheld, filters).length,
    allHiddenByTestFilter: shown.length === 0 && withheld.length > 0
  };
}

/** Keep the open detail when the filters still show it, otherwise open the first row. */
export function selectTeamMemberId(members: StaffMember[], currentId: string): string {
  if (members.some((member) => member.id === currentId)) return currentId;
  return members[0]?.id ?? "";
}

/**
 * Group headings appear only when both groups are on screen. With the default
 * "jen aktivní" filter a single "Aktivní tým" heading over the whole list is
 * noise; the moment a deactivated account joins the list it must never read
 * as a working parťák.
 */
export function teamRosterGroups(members: StaffMember[]): {
  active: StaffMember[];
  inactive: StaffMember[];
  showHeadings: boolean;
} {
  const active = members.filter((member) => member.isActive);
  const inactive = members.filter((member) => !member.isActive);
  return { active, inactive, showHeadings: active.length > 0 && inactive.length > 0 };
}

export function TeamActionFeedback(props: {
  state: TeamActionState;
  message: string | null;
  t: (key: string) => string;
}) {
  return (
    <div className="admin-team-feedback" aria-live="polite">
      {props.state === "working" && <p>{props.t("staffConsole.adminTeam.working")}</p>}
      {props.state === "done" && props.message && <p className="success-message">{props.message}</p>}
      {props.state === "error" && (
        <p className="error-message" role="alert">{props.message ?? props.t("staffConsole.adminTeam.error")}</p>
      )}
    </div>
  );
}

export function AdminTeam(props: {
  t: (key: string) => string;
  locale: Locale;
  csrfToken: string;
  /** The WHOLE team. The panel applies the global test filter itself, because
   * the count that reports it has to be measured against the same filters as
   * the list it stands over (S3-02). */
  roster: Roster;
  hideTestTenants: boolean;
  onHideTestTenantsChange: (hide: boolean) => void;
  refresh: () => Promise<void>;
  onOpenClient?: (clientId: string) => void;
  activityLabel?: (entry: TeamMemberActivity) => string;
}) {
  const { t, roster } = props;
  const [query, setQuery] = useState("");
  const [role, setRole] = useState<"all" | StaffMember["role"]>("all");
  const [activity, setActivity] = useState<"all" | "active" | "inactive">("active");
  const [selectedId, setSelectedId] = useState("");

  const split = teamRosterSplit(
    roster.members,
    { query, role, activity, locale: props.locale },
    props.hideTestTenants
  );
  const matching = split.matching;
  const groups = teamRosterGroups(matching);
  const openId = selectTeamMemberId(matching, selectedId);
  const hiddenMemberCount = split.hidden;
  const allHiddenByTestFilter = split.allHiddenByTestFilter;
  // S-11, unfixed in round one: the header counted the members left AFTER the
  // global test filter, so a panel an administrator was looking at announced
  // "Administrátoři: 0". The header is not a summary of the list, it is the
  // composition of the TEAM, and the team is what the server counts (the same
  // number the deactivation lock already trusted). The list explains itself a
  // few rows down, through the one filter sentence Servis uses everywhere.
  const activeCount = roster.summary.adminCount;
  const partnerCount = roster.summary.partnerCount;

  const onChanged = useCallback(async () => {
    await roster.reload();
    await props.refresh();
  }, [props, roster]);

  return (
    // F138d: the panel's summary above carries the name and the sentence; the
    // census stays, because it is a FACT about the team, not a heading.
    <section className="admin-team">
      <div className="admin-team-head">
        <span className="team-counts">
          {t("staffConsole.team.adminCount").replace("{count}", String(activeCount))}
          {" · "}
          {t("staffConsole.team.partnerCount").replace("{count}", String(partnerCount))}
        </span>
      </div>

      <details className="admin-team-add">
        <summary>{t("staffConsole.team.add")}</summary>
        <AddMemberForm t={t} locale={props.locale} csrfToken={props.csrfToken} onDone={roster.reload} />
      </details>

      <div className="team-filters">
        <label>
          <span>{t("staffConsole.team.search")}</span>
          <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} />
        </label>
        <label>
          <span>{t("staffConsole.team.filterRole")}</span>
          <select value={role} onChange={(event) => setRole(event.target.value as typeof role)}>
            <option value="all">{t("staffConsole.team.allRoles")}</option>
            <option value="admin">{t("staffConsole.role.admin")}</option>
            <option value="partner">{t("staffConsole.role.partner")}</option>
          </select>
        </label>
        <label>
          <span>{t("staffConsole.team.filterActivity")}</span>
          <select value={activity} onChange={(event) => setActivity(event.target.value as typeof activity)}>
            <option value="active">{t("staffConsole.team.onlyActive")}</option>
            <option value="inactive">{t("staffConsole.team.onlyInactive")}</option>
            <option value="all">{t("staffConsole.team.allActivity")}</option>
          </select>
        </label>
      </div>

      <div className="admin-team-split">
        <div className="admin-team-roster" role="group" aria-labelledby="admin-team-title">
          {/* The note explains the LIST, so it stands where the list starts.
              At the end of a hundred and fifty rows it was 780 px away from
              the count it reconciles, and the reader met the gap long before
              the explanation (audit S-11). */}
          <TestFilterNote shown={matching.length} hidden={hiddenMemberCount} t={t}
            onShowTest={() => props.onHideTestTenantsChange(false)} />
          {roster.loading && roster.members.length === 0 && <p>{t("common.loading")}</p>}
          {/* An empty list must say WHICH filter emptied it: the global test
              filter (with the way out) or the local search (audit a3-05). */}
          {!roster.loading && !matching.length && (
            <p className="staff-empty">
              {allHiddenByTestFilter ? t("staffConsole.team.allFiltered") : t("staffConsole.team.noResults")}
            </p>
          )}
          {groups.showHeadings && <h4>{t("staffConsole.team.activeHeading")}</h4>}
          {groups.active.map((member) => (
            <TeamMemberRow key={member.id} member={member} selected={member.id === openId}
              onSelect={() => setSelectedId(member.id)} t={t} />
          ))}
          {groups.showHeadings && <h4>{t("staffConsole.team.inactiveHeading")}</h4>}
          {groups.inactive.map((member) => (
            <TeamMemberRow key={member.id} member={member} selected={member.id === openId}
              onSelect={() => setSelectedId(member.id)} t={t} />
          ))}
        </div>
        <div className="admin-team-detail">
          {openId
            ? (
              <AdminTeamMemberDetail key={openId} userId={openId} t={t} locale={props.locale}
                csrfToken={props.csrfToken} adminCount={roster.summary.adminCount}
                hideTestTenants={props.hideTestTenants} onChanged={onChanged}
                onOpenClient={props.onOpenClient} activityLabel={props.activityLabel}
                // Deactivating under the default "jen aktivní" filter would
                // drop the row and take the confirmation with it. Widen the
                // filter so the member visibly MOVES to the inactive group.
                onAccountStateChanged={() => setActivity("all")} />
            )
            : <p className="staff-empty">{t("staffConsole.adminTeam.selectMember")}</p>}
        </div>
      </div>
    </section>
  );
}

function TeamMemberRow(props: {
  member: StaffMember;
  selected: boolean;
  onSelect: () => void;
  t: (key: string) => string;
}) {
  const { member, t } = props;
  return (
    <button
      type="button"
      className={member.isActive ? "team-member-row" : "team-member-row inactive"}
      aria-pressed={props.selected}
      onClick={props.onSelect}
    >
      <span className="member-avatar" aria-hidden="true">{(member.displayName ?? member.email).slice(0, 1).toUpperCase()}</span>
      <span className="member-meta">
        <strong>
          {member.displayName ?? member.email}
          {member.self && <span className="you-tag">{t("staffConsole.team.you")}</span>}
        </strong>
        <small>{member.email}</small>
        <span className="member-facts">
          <span className={`role-badge ${member.role}`}>{t(`staffConsole.role.${member.role}`)}</span>
          {!member.isActive && <span className="status-chip off">{t("staffConsole.team.inactive")}</span>}
          <span className="member-fact">{`${t("staffConsole.team.clients")}: ${member.clientCount}`}</span>
          <span className={member.profileComplete ? "member-fact ok" : "member-fact warn"}>
            {member.profileComplete ? t("staffConsole.team.profileOk") : t("staffConsole.team.profileMissing")}
          </span>
        </span>
      </span>
    </button>
  );
}

export function AdminTeamMemberDetail(props: {
  userId: string;
  t: (key: string) => string;
  locale: Locale;
  csrfToken: string;
  adminCount: number;
  hideTestTenants: boolean;
  onChanged: () => Promise<void>;
  onOpenClient?: (clientId: string) => void;
  /** Fired after a deactivate or reactivate so the roster can keep the row on
   * screen; the confirmation lives in this component and must not unmount. */
  onAccountStateChanged?: () => void;
  /** Activity wording stays in the activity module (wave 5 owns that map);
   * the detail only asks for a label so the two feeds never drift apart. */
  activityLabel?: (entry: TeamMemberActivity) => string;
  initialDetail?: TeamMemberDetail;
}) {
  const { t } = props;
  const activityLabel = props.activityLabel ?? (() => t("staffConsole.activity.unknown"));
  const [detail, setDetail] = useState<TeamMemberDetail | null>(props.initialDetail ?? null);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">(props.initialDetail ? "ready" : "loading");
  const [state, setState] = useState<TeamActionState>("idle");
  const [message, setMessage] = useState<string | null>(null);
  const [heir, setHeir] = useState("");
  const [heirOpen, setHeirOpen] = useState(false);

  const load = useCallback(async () => {
    setLoadState((current) => (current === "ready" ? current : "loading"));
    try {
      const response = await fetch(`/api/admin/team/${props.userId}`, { credentials: "include" });
      if (!response.ok) throw new Error("member_detail_failed");
      setDetail(await response.json() as TeamMemberDetail);
      setLoadState("ready");
    } catch {
      setLoadState("error");
    }
  }, [props.userId]);

  useEffect(() => { if (!props.initialDetail) void load(); }, [load, props.initialDetail]);

  if (loadState === "loading") return <p>{t("common.loading")}</p>;
  if (loadState === "error" || !detail) {
    return (
      <div className="admin-team-detail-error">
        <p className="error-message" role="alert">{t("staffConsole.adminTeam.loadError")}</p>
        <button className="secondary-button compact-button" type="button" onClick={() => void load()}>
          {t("common.retry")}
        </button>
      </div>
    );
  }

  const { member, access } = detail;
  const name = member.displayName ?? member.email;

  async function act(
    path: string,
    method: "POST" | "PATCH",
    body: unknown,
    doneMessage: string,
    onConflict?: (payload: { error?: string }) => string
  ) {
    setState("working");
    setMessage(null);
    const response = await staffMutation(path, { method, csrfToken: props.csrfToken, body });
    if (response.status === 409) {
      const payload = await response.json().catch(() => ({})) as { error?: string };
      setState("error");
      setMessage(onConflict ? onConflict(payload) : t("staffConsole.adminTeam.error"));
      return false;
    }
    if (!response.ok) {
      setState("error");
      setMessage(t(staffMutationErrorKey(response, "staffConsole.adminTeam.error")));
      return false;
    }
    setState("done");
    setMessage(doneMessage);
    await load();
    await props.onChanged();
    return true;
  }

  const changeRole = (nextRole: string) => act(
    `/api/admin/staff/${member.id}/role`, "PATCH", { role: nextRole },
    t(nextRole === "admin" ? "staffConsole.adminTeam.done.roleAdmin" : "staffConsole.adminTeam.done.rolePartner")
      .replace("{name}", name),
    () => t("staffConsole.team.roleBlocked")
  );

  const deactivate = async () => {
    const done = await act(
      `/api/admin/staff/${member.id}/deactivate`, "POST", heir ? { reassignTo: heir } : {},
      t(heir ? "staffConsole.adminTeam.done.deactivatedWithHeir" : "staffConsole.adminTeam.done.deactivated")
        .replace("{name}", name)
        .replace("{heir}", access.heirs.find((candidate) => candidate.id === heir)?.name ?? ""),
      (payload) => {
        if (payload.error === "has_clients") {
          setHeirOpen(true);
          return t("staffConsole.team.needHeir");
        }
        return t("staffConsole.team.lastAdmin");
      }
    );
    if (done) props.onAccountStateChanged?.();
  };

  const reactivate = async () => {
    const done = await act(
      `/api/admin/staff/${member.id}/reactivate`, "POST", {},
      t("staffConsole.adminTeam.done.reactivated").replace("{name}", name)
    );
    if (done) props.onAccountStateChanged?.();
  };

  const sendLink = () => act(
    `/api/admin/users/${member.id}/magic-link`, "POST", {},
    t("staffConsole.adminTeam.done.linkSent").replace("{name}", name).replace("{email}", member.email)
  );

  const revokeSessions = () => act(
    `/api/admin/users/${member.id}/revoke-sessions`, "POST", {},
    t("staffConsole.adminTeam.done.sessionsRevoked").replace("{name}", name)
  );

  const visibleClients = props.hideTestTenants ? detail.clients.filter((client) => !client.isTest) : detail.clients;
  const visibleActivity = props.hideTestTenants ? detail.activity.filter((entry) => !entry.isTest) : detail.activity;
  // S2-02: the same filter as the roster four hundred pixels to the left. The
  // picker used to offer fifty-four candidates and all fifty-four were dev
  // seeds, in the very panel that had just hidden them from the list. Handing
  // over clients cannot be undone, and this control led straight into a test
  // account.
  const visibleHeirs = props.hideTestTenants ? access.heirs.filter((candidate) => !candidate.isTest) : access.heirs;
  const hiddenHeirs = access.heirs.length - visibleHeirs.length;

  return (
    <article className="admin-team-member">
      <header className="admin-team-member-head">
        <span className="member-avatar" aria-hidden="true">{name.slice(0, 1).toUpperCase()}</span>
        <div>
          <h4>
            {name}
            {member.self && <span className="you-tag">{t("staffConsole.team.you")}</span>}
          </h4>
          <p className="admin-team-member-email">{member.email}</p>
          <p className="member-facts">
            <span className={`role-badge ${member.role}`}>{t(`staffConsole.role.${member.role}`)}</span>
            {!member.isActive && <span className="status-chip off">{t("staffConsole.team.inactive")}</span>}
            <span className="member-fact">
              {`${t("staffConsole.adminTeam.memberSince")}: ${formatDate(member.createdAt, props.locale)}`}
            </span>
          </p>
        </div>
      </header>

      <section className="admin-team-block">
        <p className="panel-label">{t("staffConsole.adminTeam.roleTitle")}</p>
        <div className="admin-team-actions">
          <label className="inline-select">
            <span>{t("staffConsole.team.role")}</span>
            <select value={member.role} disabled={state === "working" || access.roleLocked || !member.isActive}
              onChange={(event) => void changeRole(event.target.value)}>
              <option value="partner">{t("staffConsole.role.partner")}</option>
              <option value="admin">{t("staffConsole.role.admin")}</option>
            </select>
          </label>
          {/* A member who still holds clients cannot be deactivated on their
              own: the handover IS the action, and it lives in the row below
              with the count and the heir picker. A second "Deaktivovat" that
              is permanently disabled until an heir is chosen is noise, not a
              safeguard, and its "open the picker" branch could never fire
              because the disabled attribute swallowed the click. */}
          {member.isActive && !member.self && !access.needsHeir && (
            <button className="secondary-button compact-button" type="button"
              disabled={state === "working" || access.deactivateBlocked !== null}
              onClick={() => void deactivate()}>
              {t("staffConsole.team.deactivate")}
            </button>
          )}
          {!member.isActive && (
            <button className="secondary-button compact-button" type="button" disabled={state === "working"}
              onClick={() => void reactivate()}>
              {t("staffConsole.team.reactivate")}
            </button>
          )}
        </div>
        {access.roleLocked && <p className="member-note">{t("staffConsole.team.lastAdminNote")}</p>}
        {access.deactivateBlocked === "self" && <p className="member-note">{t("staffConsole.adminTeam.selfNote")}</p>}
        {(heirOpen || (access.needsHeir && member.isActive && !member.self)) && (
          <div className="reassign-row">
            <label className="inline-select">
              <span>{t("staffConsole.team.reassignTo")}</span>
              <select value={heir} onChange={(event) => setHeir(event.target.value)}>
                <option value="">{t("staffConsole.team.choose")}</option>
                {visibleHeirs.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>{`${candidate.name} · ${candidate.email}`}</option>
                ))}
              </select>
            </label>
            <button className="secondary-button compact-button" type="button"
              disabled={state === "working" || !heir || access.deactivateBlocked !== null}
              onClick={() => void deactivate()}>
              {t("staffConsole.team.confirmReassign")}
            </button>
            <p className="admin-tool-hint">
              {t("staffConsole.adminTeam.heirHint").replace("{count}", String(member.clientCount))}
            </p>
            {/* Same sentence, same way back as every other hidden row in
                Servis. The picker can reach the filter through the roster it
                sits beside, so it counts without offering the switch twice. */}
            <TestFilterNote shown={visibleHeirs.length} hidden={hiddenHeirs} t={t} />
            {visibleHeirs.length === 0 && hiddenHeirs > 0 && (
              <p className="admin-tool-hint warn">{t("staffConsole.team.heirsAllFiltered")}</p>
            )}
          </div>
        )}
      </section>

      <MemberProfileForm detail={detail} t={t} csrfToken={props.csrfToken}
        onSaved={async () => { await load(); await props.onChanged(); }} />

      <section className="admin-team-block">
        <p className="panel-label">{t("staffConsole.adminTeam.clientsTitle")}</p>
        {visibleClients.length === 0
          ? <p className="staff-empty">{t("staffConsole.adminTeam.clientsEmpty")}</p>
          : (
            <ul className="admin-team-clients">
              {visibleClients.map((client) => (
                <li key={client.id}>
                  <span>{client.name}</span>
                  <small>{client.email}</small>
                  {/* S3-07: the same control, the same height, the same way of
                      staying inside the app as the other three. */}
                  <OpenClientLink clientId={client.id} onOpenClient={props.onOpenClient}
                    label={t("staffConsole.adminTeam.openClient")} />
                </li>
              ))}
            </ul>
          )}
      </section>

      <section className="admin-team-block">
        <p className="panel-label">{t("staffConsole.adminTeam.activityTitle")}</p>
        {visibleActivity.length === 0
          ? <p className="staff-empty">{t("staffConsole.adminTeam.activityEmpty")}</p>
          : (
            <ul className="admin-team-activity">
              {visibleActivity.map((entry) => (
                <li key={entry.id}>
                  <span className="activity-type">{activityLabel(entry)}</span>
                  {/* This row is about a MEMBER, so the missing thing is the
                      client, not a detail page: "bez klienta", not the
                      timeline's own `activity.noTarget`. */}
                  <span className="activity-meta">
                    {entry.clientName ?? t("staffConsole.adminTeam.activityNoClient")}
                  </span>
                  <time dateTime={entry.createdAt}>{formatDate(entry.createdAt, props.locale)}</time>
                </li>
              ))}
            </ul>
          )}
      </section>

      <section className="admin-team-block">
        <p className="panel-label">{t("staffConsole.adminTeam.accessTitle")}</p>
        <ul className="admin-team-facts">
          <li>
            <span>{t("staffConsole.team.lastSeen")}</span>
            <strong>{access.lastSeenAt ? formatDate(access.lastSeenAt, props.locale) : t("staffConsole.team.never")}</strong>
          </li>
          <li>
            <span>{t("staffConsole.adminTeam.activeSessions")}</span>
            <strong>{String(access.activeSessions)}</strong>
          </li>
          <li>
            <span>{t("staffConsole.team.clients")}</span>
            <strong>{String(member.clientCount)}</strong>
          </li>
        </ul>
        <div className="admin-team-actions">
          <button className="secondary-button compact-button" type="button"
            disabled={state === "working" || !member.isActive} onClick={() => void sendLink()}>
            {t("staffConsole.team.sendLink")}
          </button>
          <button className="text-button compact-button" type="button"
            disabled={state === "working" || access.activeSessions === 0} onClick={() => void revokeSessions()}>
            {t("staffConsole.adminTeam.revokeSessions")}
          </button>
        </div>
      </section>

      <TeamActionFeedback state={state} message={message} t={t} />
    </article>
  );
}

/** The newer version of the row, as the server found it (S3-01). */
export interface ProfileConflict {
  photoUrl: string | null;
  calendlyUrl: string | null;
  publicEmail: string | null;
  telegramUsername: string | null;
  telegramId: string | null;
  googleAccount: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

export function MemberProfileForm(props: {
  detail: TeamMemberDetail;
  t: (key: string) => string;
  csrfToken: string;
  onSaved: () => Promise<void>;
}) {
  const { t } = props;
  const [values, setValues] = useState({
    photoUrl: props.detail.profile.photoUrl ?? "",
    calendlyUrl: props.detail.profile.calendlyUrl ?? "",
    publicEmail: props.detail.profile.publicEmail ?? "",
    telegramUsername: props.detail.profile.telegramUsername ?? "",
    telegramId: props.detail.profile.telegramId ?? "",
    googleAccount: props.detail.profile.googleAccount ?? ""
  });
  // 🔴 S3-01: which version of the row this form is looking at. It travels with
  // every save and moves ONLY on a save this form made, or when the admin
  // deliberately takes the newer version below.
  const [observedUpdatedAt, setObservedUpdatedAt] = useState(props.detail.profile.updatedAt ?? "");
  const [conflict, setConflict] = useState<ProfileConflict | null>(null);
  const [state, setState] = useState<TeamActionState>("idle");
  const [message, setMessage] = useState<string | null>(null);
  const name = props.detail.member.displayName ?? props.detail.member.email;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setState("working");
    setMessage(null);
    setConflict(null);
    const response = await staffMutation(`/api/admin/team/${props.detail.member.id}/profile`, {
      method: "PUT", csrfToken: props.csrfToken, body: { ...values, observedUpdatedAt }
    });
    if (response.status === 409) {
      // Somebody saved between this form loading and this click. Nothing was
      // written, and the panel says whose work it would have erased instead of
      // reporting a green success over the erasure.
      const payload = await response.json().catch(() => ({})) as { reason?: string; profile?: ProfileConflict };
      setState("error");
      if (payload.reason === "profile_moved" && payload.profile) {
        setConflict(payload.profile);
        setMessage(t("staffConsole.adminTeam.profileMoved")
          .replace("{editor}", payload.profile.updatedBy ?? t("staffConsole.activity.systemActor")));
        return;
      }
      setMessage(t("staffConsole.adminTeam.error"));
      return;
    }
    if (!response.ok) {
      setState("error");
      setMessage(t(staffMutationErrorKey(
        response,
        response.status === 400 ? "staffConsole.adminTeam.profileInvalid" : "staffConsole.adminTeam.error"
      )));
      return;
    }
    // The stamp the NEXT save has to present, so a second edit in the same
    // session is not refused by the guard that just let the first one through.
    const saved = await response.json().catch(() => ({})) as { updatedAt?: string };
    if (saved.updatedAt) setObservedUpdatedAt(saved.updatedAt);
    setState("done");
    setMessage(t("staffConsole.adminTeam.done.profileSaved").replace("{name}", name));
    await props.onSaved();
  }

  /** Take the newer version into the form: the one way out of the conflict. */
  function adoptNewer(newer: ProfileConflict) {
    setValues({
      photoUrl: newer.photoUrl ?? "",
      calendlyUrl: newer.calendlyUrl ?? "",
      publicEmail: newer.publicEmail ?? "",
      telegramUsername: newer.telegramUsername ?? "",
      telegramId: newer.telegramId ?? "",
      googleAccount: newer.googleAccount ?? ""
    });
    setObservedUpdatedAt(newer.updatedAt ?? "");
    setConflict(null);
    setState("idle");
    setMessage(null);
  }

  return (
    <section className="admin-team-block">
      <p className="panel-label">{t("staffConsole.staffProfile")}</p>
      {props.detail.missingProfileFields.length > 0 && (
        <p className="admin-tool-hint warn">
          {t("staffConsole.adminTeam.profileMissingFields")
            .replace("{fields}", props.detail.missingProfileFields
              .map((field) => t(`staffConsole.adminTeam.profileField.${field}`))
              .join(", "))}
        </p>
      )}
      <form className="admin-tool-form" onSubmit={submit}>
        <label>
          <span>{t("staffConsole.photoUrl")}</span>
          <input type="url" value={values.photoUrl}
            onChange={(event) => setValues({ ...values, photoUrl: event.target.value })} />
        </label>
        <p className="admin-tool-hint">{t("staffConsole.photoQualityHint")}</p>
        {values.photoUrl && (
          <div className="staff-photo-crop-preview">
            <img src={values.photoUrl} alt={t("staffConsole.photoPreviewAlt")} />
            <span>{t("staffConsole.photoPreviewCaption")}</span>
          </div>
        )}
        <label>
          <span>{t("staffConsole.calendlyUrl")}</span>
          <input type="url" value={values.calendlyUrl}
            onChange={(event) => setValues({ ...values, calendlyUrl: event.target.value })} />
        </label>
        <label>
          <span>{t("staffConsole.publicEmail")}</span>
          <input type="email" value={values.publicEmail}
            onChange={(event) => setValues({ ...values, publicEmail: event.target.value })} />
        </label>
        <label>
          <span>{t("staffConsole.adminTeam.googleAccount")}</span>
          <input type="email" value={values.googleAccount}
            onChange={(event) => setValues({ ...values, googleAccount: event.target.value })} />
        </label>
        <p className="admin-tool-hint">{t("staffConsole.adminTeam.googleAccountHint")}</p>
        <label>
          <span>{t("staffConsole.adminTeam.telegramUsername")}</span>
          <input value={values.telegramUsername}
            onChange={(event) => setValues({ ...values, telegramUsername: event.target.value })} />
        </label>
        <label>
          <span>{t("staffConsole.adminTeam.telegramId")}</span>
          <input inputMode="numeric" value={values.telegramId}
            onChange={(event) => setValues({ ...values, telegramId: event.target.value })} />
        </label>
        <p className="admin-tool-hint">{t("staffConsole.adminTeam.telegramHint")}</p>
        <button className="primary-button" type="submit" disabled={state === "working"}>
          {t("staffConsole.save")}
        </button>
        <TeamActionFeedback state={state} message={message} t={t} />
        {conflict && (
          <div className="admin-team-conflict" role="group"
            aria-label={t("staffConsole.adminTeam.profileConflictTitle")}>
            <p>{t("staffConsole.adminTeam.profileConflictBody")}</p>
            <ul className="admin-team-conflict-fields">
              {CONFLICT_FIELDS.map(({ key, labelKey }) => (
                <li key={key}>
                  <span>{t(labelKey)}</span>
                  <strong>{conflict[key] ?? t("staffConsole.adminTeam.profileFieldEmpty")}</strong>
                </li>
              ))}
            </ul>
            <button className="secondary-button compact-button" type="button"
              onClick={() => adoptNewer(conflict)}>
              {t("staffConsole.adminTeam.profileTakeNewer")}
            </button>
          </div>
        )}
      </form>
    </section>
  );
}

/** The three fields the panel itself calls client-facing, plus the identities:
 * what the newer version holds, so the admin can see what they would erase.
 * F146 dropped the description; a save can no longer erase one. */
const CONFLICT_FIELDS: Array<{ key: keyof Omit<ProfileConflict, "updatedAt" | "updatedBy">; labelKey: string }> = [
  { key: "photoUrl", labelKey: "staffConsole.photoUrl" },
  { key: "calendlyUrl", labelKey: "staffConsole.calendlyUrl" },
  { key: "publicEmail", labelKey: "staffConsole.publicEmail" },
  { key: "googleAccount", labelKey: "staffConsole.adminTeam.googleAccount" },
  { key: "telegramUsername", labelKey: "staffConsole.adminTeam.telegramUsername" },
  { key: "telegramId", labelKey: "staffConsole.adminTeam.telegramId" }
];

export function AddMemberForm(props: {
  t: (key: string) => string;
  locale: Locale;
  csrfToken: string;
  onDone: () => Promise<void>;
}) {
  const [values, setValues] = useState({ email: "", displayName: "", role: "partner", sendInvite: true });
  const [state, setState] = useState<TeamActionState>("idle");
  const [message, setMessage] = useState<string | null>(null);
  const [invite, setInvite] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setState("working");
    setMessage(null);
    setInvite(null);
    const response = await staffMutation("/api/admin/staff", {
      method: "POST", csrfToken: props.csrfToken, body: { ...values, locale: props.locale }
    });
    if (response.status === 409) {
      setState("error");
      setMessage(props.t("staffConsole.team.exists"));
      return;
    }
    if (!response.ok) {
      setState("error");
      setMessage(props.t(staffMutationErrorKey(response, "staffConsole.adminTeam.error")));
      return;
    }
    const body = await response.json() as { inviteLink?: string };
    setInvite(body.inviteLink ?? null);
    setState("done");
    setMessage(props.t(values.sendInvite
      ? "staffConsole.adminTeam.done.memberInvited"
      : "staffConsole.adminTeam.done.memberAdded")
      .replace("{name}", values.displayName)
      .replace("{email}", values.email));
    setValues({ email: "", displayName: "", role: "partner", sendInvite: true });
    await props.onDone();
  }

  return (
    <form className="admin-tool-form team-add" onSubmit={submit}>
      <label htmlFor="add-member-email"><span>{props.t("staffConsole.team.email")}</span></label>
      <input id="add-member-email" type="email" placeholder={props.t("staffConsole.team.emailExample")}
        value={values.email} onChange={(event) => setValues({ ...values, email: event.target.value })} required />
      <label htmlFor="add-member-name"><span>{props.t("staffConsole.team.name")}</span></label>
      <input id="add-member-name" placeholder={props.t("staffConsole.team.nameExample")}
        value={values.displayName} onChange={(event) => setValues({ ...values, displayName: event.target.value })} required />
      <label className="inline-select">
        <span>{props.t("staffConsole.team.role")}</span>
        <select value={values.role} onChange={(event) => setValues({ ...values, role: event.target.value })}>
          <option value="partner">{props.t("staffConsole.role.partner")}</option>
          <option value="admin">{props.t("staffConsole.role.admin")}</option>
        </select>
      </label>
      <label className="checkbox-row">
        <input type="checkbox" checked={values.sendInvite}
          onChange={(event) => setValues({ ...values, sendInvite: event.target.checked })} />
        <span>{props.t("staffConsole.team.sendInvite")}</span>
      </label>
      <button className="primary-button" type="submit" disabled={state === "working"}>
        {props.t("staffConsole.team.add")}
      </button>
      <TeamActionFeedback state={state} message={message} t={props.t} />
      {invite && <p className="invite-reveal">{props.t("staffConsole.team.inviteReady")} <code>{invite}</code></p>}
    </form>
  );
}
